import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { NavLink, Location, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';

import { PopupType } from '@/types';
import { ctxUser } from '@/contexts/UserContext';
import { chatContext } from '@/contexts/ChatContext';
import { MenuHeader } from '@/components/home/MenuCloneIcons';

import './Home.css';
import { ctxPopup } from '@/contexts/PopupContext';

function isSubmenuSelected(location: Location, popup: PopupType) {
  return popup.children?.find((p) => location.pathname.endsWith(p.path)) !== undefined;
}

function getActiveParentRoute(p: PopupType, location: Location) {
  if (!p.activeOnSubRoutes) return undefined;
  if (p.path.startsWith('/')) return p.path;

  const path = p.path.replace(/^\/+|\/+$/g, '');
  const parentPath = `/${path}`;
  const index = location.pathname.indexOf(`${parentPath}/`);

  if (index === -1) return undefined;

  return location.pathname.substring(0, index + parentPath.length);
}

// olyan menupont, aminek van gyereke
// klikk-re kinyilik/becsukodik
// tobb submenu is lehet, az egyik kinyitasa nem csukja be a tobbit
// ha nyitott, kilistazza a gyerek menupontokat
function Submenu({ popup, drawerMenuOpen }: { popup: PopupType; drawerMenuOpen: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const className = `home-left-nav-item${isSubmenuSelected(location, popup) ? ' Mui-selected' : ''} ${
    isOpen && !drawerMenuOpen ? ' opened' : ''
  }`;

  return (
    <ButtonBase
      onClick={(e) => {
        setAnchorEl(e.currentTarget);
        setOpen((current) => !current);
      }}
      disableRipple
      sx={{ width: '100%', display: 'block', textAlign: 'left' }}
    >
      <Stack
        direction="row"
        sx={{ gap: 2, alignContent: 'center', borderRadius: 2, cursor: 'pointer', userSelect: 'none' }}
        data-qa-element-id={`left-navigation-action-${popup.title}`}
        className={className}
      >
        <ListItemIcon sx={{ minWidth: 45, justifyContent: 'center', alignItems: 'center', height: 48 }}>
          {popup.icon}
        </ListItemIcon>
        {drawerMenuOpen && (
          <Stack direction="row" sx={{ justifyContent: 'space-between', width: '100%', alignItems: 'center', pr: 1 }}>
            <ListItemText data-qa-element-id={`menu_item_text_${popup.title}`} primary={t(popup.title!)} />
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </Stack>
        )}
      </Stack>
      {popup.children?.length && (
        <>
          {drawerMenuOpen ? (
            <Collapse in={isOpen && !!popup.children.length} timeout="auto" unmountOnExit>
              <Stack data-qa-element-id={`submenu_for_${popup.title}`} sx={{ borderRadius: 2 }}>
                <IconList
                  popups={popup.children}
                  drawerMenuOpen={drawerMenuOpen}
                  withoutIcons={true}
                  className="home-left-submenu"
                  isSubMenu={true}
                />
              </Stack>
            </Collapse>
          ) : (
            <SideSubmenu
              popup={popup}
              isOpen={isOpen && !!popup.children.length}
              setOpen={setOpen}
              anchorEl={anchorEl}
            />
          )}
        </>
      )}
    </ButtonBase>
  );
}

function SideSubmenu({
  popup,
  isOpen,
  setOpen,
  anchorEl,
}: {
  popup: PopupType;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  anchorEl: HTMLElement | null;
}) {
  const { t } = useTranslation();
  if (!popup.children?.length) return;

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: { backgroundColor: 'transparent', boxShadow: 'none', borderRadius: '0' },
        },
      }}
    >
      <Stack
        sx={{
          ml: 1.5,
          backgroundColor: 'primary.dark',
          color: 'white',
          borderRadius: 2,
          width: 200,
          overflow: 'hidden',
          border: 2,
          borderColor: '#fff7',
        }}
        data-qa-element-id={`popup_menu_for_${popup.title}`}
      >
        <Typography variant="body2" sx={{ color: 'white', opacity: 0.6, p: 1 }}>
          {t(popup.title!)}
        </Typography>
        <Divider sx={{ borderColor: 'white', opacity: 0.2 }} />
        <IconList
          popups={popup.children}
          drawerMenuOpen={true}
          setOpen={setOpen}
          withoutIcons={true}
          className="home-left-nav-side-submenu"
          isSubMenu={true}
        />
      </Stack>
    </Popover>
  );
}

function getTo(p: PopupType, location: Location) {
  const activeParentRoute = getActiveParentRoute(p, location);
  if (activeParentRoute) return activeParentRoute;

  if (p.level === 1) {
    // ha a path vagy a navigateTo az aktualis akkor legyek aktiv
    if (location.pathname === p.path) return location.pathname;
    if (location.pathname === p.navigateTo) return location.pathname;
    // egyebkent tok mindegy, ugysem hasznaljuk
    return p.path;
  }
  // masodik szint eseten
  if (location.pathname.endsWith(p.path)) return location.pathname; // ha en vagyok a vegen az egesz legyen az aktualis h aktiv legyek
  return p.path; // egyebkent tok mindegy, ugysem hasznaljuk
}

function getNav(p: PopupType, location: Location) {
  if (p.level === 1) {
    // ha aktiv vagyok pont a masikra kell menni :(
    if (location.pathname === p.path) return p.navigateTo!;
    if (location.pathname === p.navigateTo) return p.path;
    // ha nem vagyok aktiv akkor az egyikre kell menni a ketto kozul
    // eldonthetjuk melyikkel induljon ilyenkor, a select vagy a select nelkulivel ?
    return p.navigateTo! || p.path;
  }

  // ha en vagyok az aktiv akkor az 1-es szintu-re menjen vissza
  if (location.pathname.endsWith(p.path))
    return location.pathname.substring(0, location.pathname.length - p.path.length - 1);
  // nem en vagyok az aktiv
  // mi van ha az elso resz nem a tracking vagy a route ?? ha a reports-rol jon ???
  const parts = location.pathname.split('/'); // regex lehet jobb lenne
  let level1path = parts[1]; // a nulladik elem ures string :(
  // ez sajnos be van egetve
  if (level1path !== 'tracking' && level1path !== 'routes') {
    level1path = 'tracking';
  }
  return '/' + level1path + '/' + p.path;
}

// olyan menupont, aminek nincs gyereke
function MenuItem({
  popup,
  drawerMenuOpen,
  setOpen,
  withoutIcons,
  isSubMenu,
}: {
  popup: PopupType;
  drawerMenuOpen: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  withoutIcons?: boolean;
  isSubMenu?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <NavLink
      to={getTo(popup, location)}
      end={!popup.activeOnSubRoutes}
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen?.(false);
        await navigate(getNav(popup, location)); // sajnos a to nem jo helyre mutat :(
      }}
      data-qa-element-id={`left-navigation-action-${popup.title}`}
      className={({ isActive }) => (isActive ? 'Mui-selected' : '')}
      style={{ width: '100%', textDecoration: 'none', color: 'white' }}
    >
      <Stack
        direction="row"
        sx={{ gap: 2, alignItems: 'center', borderRadius: isSubMenu ? 0 : 2 }}
        className={isSubMenu ? 'home-left-nav-sub-item' : 'home-left-nav-item'}
      >
        {!withoutIcons && (
          <ListItemIcon sx={{ minWidth: 45, justifyContent: 'center', alignItems: 'center', height: 48 }}>
            {popup.icon}
          </ListItemIcon>
        )}

        {drawerMenuOpen && (
          <ListItemText
            data-qa-element-id={`menu_item_text_${popup.title}`}
            primary={t(popup.title!)}
            sx={{ m: 0, color: isSubMenu ? '#fffc !important' : '#fff', ':hover': { color: '#fff !important' } }}
          />
        )}
      </Stack>
    </NavLink>
  );
}

function IconList({
  popups,
  drawerMenuOpen,
  setOpen,
  className,
  withoutIcons,
  isSubMenu,
}: {
  popups: PopupType[];
  drawerMenuOpen: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  withoutIcons?: boolean;
  isSubMenu?: boolean;
}) {
  const userCtx = useContext(ctxUser);
  const chatCtx = useContext(chatContext);
  const [id, setId] = useState<string | undefined>(chatCtx?.chatId);

  useEffect(() => {
    setId(chatCtx?.chatId);
  }, [chatCtx?.chatId]);

  function hasExtraFeature(popup: PopupType) {
    if (!popup.requiredExtraFeature) return true;
    return userCtx?.hasExtraFeature(popup.requiredExtraFeature);
  }

  const filtered = popups
    .filter((p) => !p.hidden)
    .filter((p) => hasExtraFeature(p))
    .filter((p) => !userCtx?.isToken() || p.showToken)
    .filter((p) => !p.show || p.show(userCtx?.user, id));

  return (
    <List disablePadding className={className}>
      {filtered.map((p) => (
        <ListItem disablePadding key={p.title} sx={{ width: '100%' }}>
          {p.children?.length ? (
            <Submenu key={drawerMenuOpen ? 'drawer-open' : 'drawer-closed'} popup={p} drawerMenuOpen={drawerMenuOpen} />
          ) : (
            <MenuItem
              popup={p}
              drawerMenuOpen={drawerMenuOpen}
              setOpen={setOpen}
              withoutIcons={withoutIcons}
              isSubMenu={isSubMenu}
            />
          )}
        </ListItem>
      ))}
    </List>
  );
}

export function HomeLeftNav({ popups }: { popups: PopupType[] }) {
  const popupCtx = useContext(ctxPopup);

  if (!popupCtx) return;
  const drawerMenuOpen = popupCtx.drawerMenuOpen;

  const mainPopups = popups.filter((p) => p.mainFunction);
  const secondaryPopups = popups.filter((p) => !p.mainFunction);

  return (
    <Box className={`home-nav ${drawerMenuOpen ? 'home-left-nav-opened' : 'home-left-nav-closed'}`}>
      <Drawer
        variant="permanent"
        className={`home-left-nav-navigator ${drawerMenuOpen ? 'home-left-nav-opened' : 'home-left-nav-closed'}`}
        classes={{ paper: drawerMenuOpen ? 'home-left-nav-opened' : 'home-left-nav-closed' }}
      >
        <div className="home-left-nav-list">
          <div className="home-left-nav-header">
            <MenuHeader drawerMenuOpen={drawerMenuOpen} />
            <Divider sx={{ bgcolor: '#fff3' }} />
          </div>
          <div className="home-left-nav-scrollable">
            <IconList popups={mainPopups} drawerMenuOpen={drawerMenuOpen} />
            <div className="home-left-nav-secondary-list">
              <Divider sx={{ bgcolor: '#fff3' }} />
              <IconList popups={secondaryPopups} drawerMenuOpen={drawerMenuOpen} />
            </div>
            <Divider sx={{ bgcolor: '#fff3' }} />
            <Button
              onClick={() => popupCtx.setDrawerMenuOpen(!drawerMenuOpen)}
              className="home-left-nav-list-close"
              data-qa-element-id="home-left-nav-expand-collapse"
            >
              {drawerMenuOpen ? (
                <KeyboardDoubleArrowLeft fontSize="medium" />
              ) : (
                <KeyboardDoubleArrowRight fontSize="medium" />
              )}
            </Button>
          </div>
        </div>
      </Drawer>
    </Box>
  );
}
